import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./style.module.css";
import Breadcrumb from "../../Components/Breadcrumb";
import {
  getAIInteractionList,
  saveAIInteraction,
  getLLMConfigurationList,
} from "../../api/common";
import { getDateFormatString } from "../../utils";
import { X } from "lucide-react";

interface LLMOption {
  id: number;
  providerName: string;
  providerType: string;
}

interface AIInteractionRow {
  interactionId: number;
  interactionType: string;
  llmConfigurationId: number | null;
  llmProvider: string | null;
  providerType: string | null;
  updatedBy: string | null;
  updatedOn: string | null;
}

const breadcrumbItems = [
  { label: "Configuration", path: "#" },
  { label: "Settings", path: "#" },
  { label: "AI Interaction Configuration" },
];

const getProviderLabel = (type: string, name?: string | null) => {
  if (type?.toLowerCase().includes("cloud") && name) {
    return `${type} (${name})`;
  }
  return type;
};

const AIInteractionConfig: React.FC = () => {
  const [data, setData] = useState<AIInteractionRow[]>([]);
  const [draftData, setDraftData] = useState<AIInteractionRow[]>([]);
  const [llmOptions, setLlmOptions] = useState<LLMOption[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const triggerRefs = useRef<Record<number, HTMLButtonElement | null>>({});
  const dropdownRef = useRef<HTMLDivElement>(null);

  const hasChanges = draftData.some((draft) => {
    const original = data.find((d) => d.interactionId === draft.interactionId);
    return original && original.llmConfigurationId !== draft.llmConfigurationId;
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getAIInteractionList();
      if (response) setData(response);
    } catch (error) {
      console.error("Failed to fetch AI interactions:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLLMOptions = async () => {
    try {
      const response = await getLLMConfigurationList("QUERY");
      if (response) setLlmOptions(response);
    } catch (error) {
      console.error("Failed to fetch LLM configurations:", error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchLLMOptions();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const isInsideTrigger = Object.values(triggerRefs.current).some(
        (ref) => ref?.contains(target)
      );
      if (!isInsideTrigger && !dropdownRef.current?.contains(target)) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleEdit = () => {
    setDraftData(data.map((row) => ({ ...row })));
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setOpenDropdownId(null);
  };

  const handleSave = async () => {
    const changedRows = draftData.filter((draft) => {
      const original = data.find((d) => d.interactionId === draft.interactionId);
      return original && original.llmConfigurationId !== draft.llmConfigurationId;
    });

    if (changedRows.length === 0) {
      setIsModalOpen(false);
      return;
    }

    try {
      const payload = changedRows.map((row) => ({
        interactionId: row.interactionId,
        llmConfigurationId: row.llmConfigurationId,
      }));
      await saveAIInteraction(payload);
      setIsModalOpen(false);
      await fetchData();
    } catch (error) {
      console.error("Failed to save AI interactions:", error);
    }
  };

  const handleProviderChange = (interactionId: number, newLlmId: number) => {
    setDraftData((prev) =>
      prev.map((row) =>
        row.interactionId === interactionId
          ? { ...row, llmConfigurationId: newLlmId }
          : row
      )
    );
    setOpenDropdownId(null);
  };

  const toggleDropdown = (interactionId: number) => {
    setOpenDropdownId(openDropdownId === interactionId ? null : interactionId);
  };

  const getSelectedLabel = (llmConfigurationId: number | null) => {
    const option = llmOptions.find((opt) => opt.id === llmConfigurationId);
    if (!option) return "-- Select --";
    return getProviderLabel(option.providerType, option.providerName);
  };

  if (loading) {
    return (
      <>
        <div className={styles.breadcrumbNoNav}>
          <Breadcrumb items={breadcrumbItems} />
        </div>
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}>Loading...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={styles.breadcrumbNoNav}>
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className={styles.editRow}>
        <button className={styles.btnEdit} onClick={handleEdit}>Edit</button>
      </div>

      <div className={styles.card}>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr className={styles.headerRow}>
                <th className={styles.thNo}>No</th>
                <th className={styles.th}>AI Features</th>
                <th className={styles.th}>Provider</th>
                <th className={styles.th}>Last Changed By</th>
                <th className={styles.th}>Last Changed On</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, index) => {
                const isCloud = (row.providerType || "").toLowerCase().includes("cloud");
                return (
                  <tr key={row.interactionId} className={styles.tr}>
                    <td className={styles.tdNo}>{index + 1}</td>
                    <td className={styles.td}>{row.interactionType}</td>
                    <td className={styles.td}>
                      <span className={`${styles.badge} ${isCloud ? styles.badgeCloud : styles.badgeSelf}`}>
                        {getProviderLabel(row.providerType || "-", row.llmProvider)}
                      </span>
                    </td>
                    <td className={styles.td}>{row.updatedBy || "-"}</td>
                    <td className={styles.td}>
                      {row.updatedOn
                        ? getDateFormatString(row.updatedOn, "DD/MM/YYYY, hh:mm A")
                        : "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && createPortal(
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <span className={styles.modalTitle}>Edit</span>
              <button type="button" className={styles.modalCloseBtn} onClick={handleCancel}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalTableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr className={styles.headerRow}>
                    <th className={styles.th}>AI Features</th>
                    <th className={styles.th}>Provider</th>
                  </tr>
                </thead>
                <tbody>
                  {draftData.map((row, index) => {
                    const label = getSelectedLabel(row.llmConfigurationId);
                    const isCloud = label.toLowerCase().includes("cloud");
                    return (
                      <tr key={row.interactionId} className={styles.tr}>
                        <td className={styles.td}>{row.interactionType}</td>
                        <td className={styles.td}>
                          <div className={styles.dropdownWrapper}>
                            <button
                              ref={(el) => { triggerRefs.current[row.interactionId] = el; }}
                              type="button"
                              className={`${styles.dropdownTrigger} ${isCloud ? styles.dropdownTriggerCloud : styles.dropdownTriggerSelf}`}
                              onClick={() => toggleDropdown(row.interactionId)}
                            >
                              {label}
                              <span className={styles.dropdownArrow}>▼</span>
                            </button>
                            {openDropdownId === row.interactionId && (
                              <div
                                ref={dropdownRef}
                                className={styles.dropdown}
                              >
                                {llmOptions.map((opt) => (
                                  <div
                                    key={opt.id}
                                    className={styles.dropdownItem}
                                    onClick={() => handleProviderChange(row.interactionId, opt.id)}
                                  >
                                    {getProviderLabel(opt.providerType, opt.providerName)}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.btnCancel} onClick={handleCancel}>Cancel</button>
              <button className={styles.btnSave} onClick={handleSave} disabled={!hasChanges}>
                Save
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default AIInteractionConfig;
